import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Repeat, Plus, Play, Pause, PlayCircle, History, Trash2, Search, CalendarClock,
  Loader2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDate, titleCase } from "@/lib/format";
import { useCanEdit } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import Pagination, { usePagedList } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_TONE = {
  ACTIVE: "bg-emerald-50 text-emerald-700",
  PAUSED: "bg-amber-50 text-amber-700",
  COMPLETED: "bg-slate-100 text-slate-600",
};

const Chip = ({ status }) => (
  <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
    STATUS_TONE[status] || "bg-slate-100 text-slate-600"}`}>
    {titleCase(status || "")}
  </span>
);

export default function RecurringInvoices() {
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [summary, setSummary] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [running, setRunning] = useState(false);

  const query = useMemo(() => ({
    q: q || undefined, status: status === "ALL" ? undefined : status,
  }), [q, status]);

  const { rows, total, page, setPage, size, setSize, loading, reload } =
    usePagedList("/recurring", query);

  const loadSummary = useCallback(() => {
    api.get("/recurring/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  const refresh = () => { reload(); loadSummary(); };

  const runOne = async (row) => {
    try {
      const { data } = await api.post(`/recurring/${row.id}/run`);
      const made = data.runs[0];
      if (!made) { toast.info("Nothing due on this schedule"); return; }
      toast.success(made.emailed ? "Invoice generated and emailed"
                  : made.issued ? "Invoice generated and issued"
                  : "Draft invoice generated");
      refresh();
      if (made.document_id) navigate(`/invoices/${made.document_id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runAll = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/recurring/run-due");
      toast.success(data.generated
        ? `${data.generated} invoice${data.generated === 1 ? "" : "s"} generated`
        : "Nothing was due");
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} schedule(s) had a problem`,
          { description: data.errors.map((e) => `${e.name}: ${e.error}`).join(" · ") });
      }
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
    setRunning(false);
  };

  const setStatusOf = async (row, next) => {
    try {
      await api.post(`/recurring/${row.id}/status`, { status: next });
      toast.success(next === "ACTIVE" ? "Resumed — next run is today" : "Paused");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Invoices already generated stay.`)) return;
    try {
      await api.delete(`/recurring/${row.id}`);
      toast.success("Schedule deleted");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const actionsFor = (row) => (
    <>
      {canEdit && row.status !== "COMPLETED" && (
        <button className="text-xs font-medium text-blue-700 hover:text-blue-900 transition-colors duration-150"
          onClick={() => runOne(row)} data-testid={`recurring-run-${row.id}`}>Run now</button>
      )}
      {canEdit && (
        <button className="text-xs text-slate-500 hover:text-slate-800 transition-colors duration-150"
          onClick={() => navigate(`/recurring/${row.id}/edit`)}
          data-testid={`recurring-edit-${row.id}`}>Edit</button>
      )}
      <button className="text-xs text-slate-500 hover:text-slate-800 transition-colors duration-150"
        onClick={() => setHistoryFor(row)} data-testid={`recurring-history-${row.id}`}>History</button>
      {canEdit && row.status === "ACTIVE" && (
        <button className="text-xs text-amber-700 hover:text-amber-900 transition-colors duration-150"
          onClick={() => setStatusOf(row, "PAUSED")}
          data-testid={`recurring-pause-${row.id}`}>Pause</button>
      )}
      {canEdit && row.status === "PAUSED" && (
        <button className="text-xs text-emerald-700 hover:text-emerald-900 transition-colors duration-150"
          onClick={() => setStatusOf(row, "ACTIVE")}
          data-testid={`recurring-resume-${row.id}`}>Resume</button>
      )}
      {canEdit && (
        <button className="text-xs text-slate-400 hover:text-rose-600 transition-colors duration-150"
          onClick={() => remove(row)} data-testid={`recurring-delete-${row.id}`}>Delete</button>
      )}
    </>
  );

  return (
    <div className="space-y-4" data-testid="recurring-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-2xl text-sm text-slate-500">
          The same invoice on a schedule — an AMC, a retainer, rent. Invoices are
          generated overnight; a schedule that falls behind catches up, each invoice
          dated the day it was actually due.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && !!summary?.due_now && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={runAll}
              disabled={running} data-testid="recurring-run-all">
              {running ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={15} />}
              Run {summary.due_now} due now
            </Button>
          )}
          {canEdit && (
            <Button size="sm" onClick={() => navigate("/recurring/new")}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="new-recurring-btn">
              <Plus size={15} /> New schedule
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[["Active", summary.active, Repeat, "text-emerald-500", "recurring-tile-active"],
            ["Due now", summary.due_now, CalendarClock,
             summary.due_now ? "text-blue-500" : "text-slate-400", "recurring-tile-due"],
            ["Paused", summary.paused, Pause, "text-amber-500", "recurring-tile-paused"],
            ["Finished", summary.completed, History, "text-slate-400", "recurring-tile-done"],
          ].map(([label, value, Icon, tone, testid]) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-white p-4"
              data-testid={testid}>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
                <Icon size={13} className={tone} /> {label}
              </div>
              <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="h-9 pl-8" placeholder="Schedule or customer" value={q}
            data-testid="recurring-search" onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-full sm:w-40" data-testid="recurring-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Any status</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="PAUSED">Paused</SelectItem>
            <SelectItem value="COMPLETED">Finished</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <TableWrap>
        <table className="w-full text-sm" data-testid="recurring-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Schedule</th>
              <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
              <th className="px-3 py-2.5 text-left font-semibold">Repeats</th>
              <th className="px-3 py-2.5 text-left font-semibold">Next</th>
              <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
              <th className="px-3 py-2.5 text-left font-semibold">On the day</th>
              <th className="px-3 py-2.5 text-left font-semibold">Status</th>
              <th className="px-4 py-2.5 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 transition-colors duration-150"
                data-testid={`recurring-row-${r.id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[10px] text-slate-400">
                    {r.occurrences_done} issued
                    {r.max_occurrences ? ` of ${r.max_occurrences}` : ""}
                  </div>
                </td>
                <td className="px-3 py-2.5">{r.party_name}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">
                  {r.interval > 1 ? `Every ${r.interval} × ` : ""}{r.frequency_label}
                </td>
                <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                  {r.next_run_date ? (
                    <span className={r.is_due ? "font-semibold text-blue-700" : "text-slate-600"}>
                      {fmtDate(r.next_run_date)}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                  {inr(r.totals?.grand_total)}
                </td>
                <td className="px-3 py-2.5 text-[11px] text-slate-500">{r.action_label}</td>
                <td className="px-3 py-2.5"><Chip status={r.status} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap items-center justify-end gap-3">{actionsFor(r)}</div>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr><td colSpan={8} className="px-4 py-16 text-center">
                <Repeat size={26} className="mx-auto text-slate-300" />
                <p className="mt-3 text-sm font-medium text-slate-700">No recurring invoices</p>
                <p className="mt-1 text-xs text-slate-500">
                  Set one up for anything you bill on the same day every month.
                </p>
              </td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <CardList empty="No recurring invoices">
        {rows.map((r) => (
          <DataCard key={r.id} testid={`recurring-card-${r.id}`}
            title={r.name} subtitle={`${r.party_name} · ${r.frequency_label}`}
            right={<>
              <div className="font-mono text-sm font-bold tabular-nums">{inr(r.totals?.grand_total)}</div>
              <div className="mt-1"><Chip status={r.status} /></div>
            </>}
            rows={[["Next", r.next_run_date ? fmtDate(r.next_run_date) : "—"],
                   ["On the day", r.action_label]]}
            actions={actionsFor(r)} />
        ))}
      </CardList>

      <Pagination total={total} page={page} setPage={setPage} size={size} setSize={setSize}
        loading={loading} testid="recurring-pagination" />

      {historyFor && (
        <HistoryDialog profile={historyFor} onClose={() => setHistoryFor(null)} />
      )}
    </div>
  );
}

function HistoryDialog({ profile, onClose }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get(`/recurring/${profile.id}/history`)
      .then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [profile.id]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="recurring-history-dialog">
        <DialogHeader>
          <DialogTitle>{profile.name}</DialogTitle>
          <DialogDescription>
            Every invoice this schedule has produced, newest first.
            {profile.upcoming?.length
              ? ` Next: ${profile.upcoming.slice(0, 3).map(fmtDate).join(", ")}.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
          {rows === null && <div className="px-3 py-6 text-center text-xs text-slate-500">Loading…</div>}
          {rows?.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-slate-500">
              Nothing generated yet.
            </div>
          )}
          {(rows || []).map((h, i) => (
            <button key={i} className="flex w-full items-center gap-3 px-3 py-2 text-left text-xs hover:bg-slate-50 transition-colors duration-150"
              onClick={() => h.document_id && navigate(`/invoices/${h.document_id}`)}>
              <span className="w-24 shrink-0 text-slate-500">{fmtDate(h.date)}</span>
              <span className="w-28 shrink-0 font-mono font-semibold">
                {h.document?.document_number || "Draft"}
              </span>
              <span className="flex-1 truncate text-slate-500">
                {h.error ? <span className="text-rose-700">{h.error}</span>
                         : h.emailed ? "issued and emailed"
                         : h.issued ? "issued" : "drafted"}
              </span>
              <span className="font-mono tabular-nums">
                {inr(h.document?.totals?.grand_total)}
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
